import { FocusContext, setFocus, useFocusable } from '@noriginmedia/norigin-spatial-navigation';
import { useDrawer } from 'common/contexts/drawer';
import { usePopup } from 'common/contexts/popup';
import { PlayerControlsState } from 'common/reducers/videoPlayer';
import { isEmpty } from 'lodash';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import BottomView from './BottomView';
import './Controls.styles';
import TopView from './TopView';

export interface PlayerControlsProps {
  toggleControls: (mini?: boolean, timeout?: number) => void;
  showControls: (mini?: boolean) => void;
  hideControls: () => void;
  togglePlay: (toPlay?: boolean) => void;
  forward: (seconds: number) => void;
  backward: (seconds: number) => void;
  setVideoToLive: () => void;
  setVideoToBegining: () => void;
  setCurrentTime: (time: number, onSeeked?: VoidFunction) => void;
  play: () => void;
  pause: () => void;
  playPrevious: (fromEnd?: number) => void;
  changeSubtitleTrack: (value: number) => void;
  changeAudioTrack: (value: number) => void;
  changeQuality: (value: number) => void;
  getDuration: () => number;
  fastZapNumber?: number;
  disabled?: boolean;
  vodSelectionPage?: boolean;
}

type IPlayerControlsProps = PlayerControlsProps & {
  isFullscreen?: boolean;
  playerControlsState: PlayerControlsState;
  hasTranslations?: boolean;
};

function PlayerControls(props: IPlayerControlsProps) {
  const { closeDrawer } = useDrawer();
  const { ref, focusKey } = useFocusable({
    focusable: !props.disabled || props.isFullscreen,
    focusKey: 'PLAYER_CONTROLS',
    preferredChildFocusKey: 'TOGGLE_PLAY',
    saveLastFocusedChild: true,
    isFocusBoundary: true,
    autoRestoreFocus: true
  });
  const { hasPopups } = usePopup();

  useEffect(() => {
    if (hasPopups || props.disabled) {
      return;
    }
    if (props.isFullscreen) {
      ['hidden', 'mini', 'seeking'].includes(props.playerControlsState) &&
        !props.vodSelectionPage &&
        setFocus('SEEKBAR');
      props.playerControlsState === 'channels' && setFocus('CHANNEL_LIST');
    }
  }, [
    props.playerControlsState,
    props.isFullscreen,
    hasPopups,
    props.disabled,
    props.vodSelectionPage
  ]);

  // Close drawer when player is not expanded or controls are hidden
  useEffect(() => {
    (props.playerControlsState === 'hidden' || !props.isFullscreen) &&
      closeDrawer('player-settings', false);
  }, [props.playerControlsState, props.isFullscreen]);

  // Skip rendering of controls when player is not fullscreen
  if (!props.hasTranslations) {
    return <></>;
  }

  return (
    <FocusContext.Provider value={focusKey}>
      <div className={`controls-container ${props.isFullscreen ? 'visible' : ''}`} ref={ref}>
        <TopView
          showControls={props.showControls}
          toggleControls={props.toggleControls}
          changeAudioTrack={props.changeAudioTrack}
          changeQuality={props.changeQuality}
          changeSubtitleTrack={props.changeSubtitleTrack}
          fastZapNumber={props.fastZapNumber}
          vodSelectionPage={props.vodSelectionPage}
        />
        <BottomView {...props} />
      </div>
    </FocusContext.Provider>
  );
}

const mapStateToProps = ({ videoPlayer, i18n }: any) => {
  return {
    playerControlsState: videoPlayer.playerControlsState,
    isFullscreen: videoPlayer.video?.isFullscreen,
    hasTranslations: !isEmpty(i18n)
  };
};
export default React.memo(connect(mapStateToProps, {})(PlayerControls));
